import { Pipe, PipeTransform } from '@angular/core';
import { ContractDurationEnumToKeys, OrderDetailField } from '@vendasta/sales-ui';
import {
  CustomerNotesMetadata,
  DeclineCancellationEvent,
  DeclinedOrderMetadata,
  HistoryMetadata,
  NotesMetadata,
  Order,
  RequestCancellationMetadata,
  SubmittedForCustomerApprovalSendingMetadata,
  UpdateContractDurationMetadata,
  UpdatedRequestedActivationMetadata,
  UpdatedTagsMetadata,
  UserInterface as SalesUser,
} from '@vendasta/sales-orders';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { DeclineReasons } from '@galaxy/billing';
import { ChargeFailureCodeMap } from '../../shared/constants';

const genericChargeError = 'generic_decline';

@Pipe({ standalone: true, name: 'buildDetailValue' })
export class BuildDetailValuePipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(orderDetailField: OrderDetailField, order: Order): string {
    const nameAndDateString = this.formatDateAndNamePair(orderDetailField.date, orderDetailField.userInfo);
    return this.buildValueFromMetadata(nameAndDateString, orderDetailField.metadata, order).replace(/\n/g, '<br>');
  }

  private formatDateAndNamePair(date: Date, userInfo: string): string {
    const formattedDate = this.formatDateWithTime(date);
    if (userInfo) {
      return this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.DATE_BY_USER', {
        date: formattedDate,
        user: userInfo,
      });
    }
    // If there's no user just display the date ()
    return formattedDate;
  }

  private getDisplayNameForSalesUser(user: SalesUser): string {
    if (!user) {
      return '';
    }
    const names = [];
    if (user.firstName) {
      names.push(user.firstName);
    }
    if (user.lastName) {
      names.push(user.lastName);
    }
    const displayName = names.join(' ');
    if (!displayName) {
      return user.email;
    }
    return displayName;
  }

  private formatDisplayName(firstName: string, lastName: string): string {
    const names = [];
    if (firstName) {
      names.push(firstName);
    }
    if (lastName) {
      names.push(lastName);
    }
    return names.join(' ');
  }

  private formatDateWithTime(date: Date): string {
    return new DatePipe('en-US').transform(date, 'MMMM d, y, h:mm:ss a');
  }

  private buildValueFromMetadata(nameAndDateString: string, metadata: HistoryMetadata, order: Order): string {
    if (
      metadata instanceof RequestCancellationMetadata ||
      metadata instanceof NotesMetadata ||
      metadata instanceof CustomerNotesMetadata
    ) {
      let notes = metadata.current;
      if (metadata.event.id === DeclineCancellationEvent) {
        notes = metadata.event.name + '\n' + metadata.current;
      }
      return nameAndDateString + '\n' + notes;
    }
    if (metadata instanceof DeclinedOrderMetadata) {
      const notes = [...metadata.declinedReasons, metadata.current].join('\n');
      let description = nameAndDateString + '\n' + notes;
      if (metadata.chargeFailureCode) {
        //take the declineCode value or the chargeFailureCode value from metadata
        let failureCode =
          DeclineReasons.get(metadata.chargeDeclineCode)?.description ||
          ChargeFailureCodeMap[metadata.chargeFailureCode];
        const nextSteps =
          DeclineReasons.get(metadata.chargeDeclineCode)?.nextSteps ||
          DeclineReasons.get(genericChargeError)?.nextSteps;
        //Fallback to generic error if it is not defined
        if (!failureCode) {
          failureCode = DeclineReasons.get(genericChargeError)?.description;
        }
        description += ': ' + this.translateService.instant(failureCode);
        if (nextSteps?.length) {
          description += ' ' + this.translateService.instant(nextSteps);
        }
      }
      return description;
    }
    if (metadata instanceof UpdatedTagsMetadata) {
      const tagsString =
        !!metadata.tags && metadata.tags.length > 0
          ? this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
              value: metadata.tags.join(', '),
            })
          : this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.ALL_TAGS_REMOVED');
      return nameAndDateString + '\n' + tagsString;
    }
    if (metadata instanceof UpdatedRequestedActivationMetadata) {
      if (!!metadata.previous && !!metadata.current) {
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: this.formatDate(metadata.previous),
            toValue: this.formatDate(metadata.current),
          })
        );
      }
      if (metadata.current) {
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
            value: this.formatDate(metadata.current),
          })
        );
      }
      if (metadata.previous) {
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: this.formatDate(metadata.previous),
            toValue: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_START_DATE'),
          })
        );
      }
      return (
        nameAndDateString +
        '\n' +
        this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
          value: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_START_DATE'),
        })
      );
    }
    if (metadata instanceof UpdateContractDurationMetadata) {
      if (!!metadata.previous && !!metadata.current) {
        const previousDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.previous.duration],
        );
        const previousDuration = `${metadata.previous.value} ${previousDurationPeriod}`;
        const currentDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.current.duration],
        );
        const currentDuration = `${metadata.current.value} ${currentDurationPeriod}`;
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: previousDuration,
            toValue: currentDuration,
          })
        );
      }
      if (metadata.current) {
        const currentDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.current.duration],
        );
        const currentDuration = `${metadata.current.value} ${currentDurationPeriod}`;
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
            value: currentDuration,
          })
        );
      }
      if (metadata.previous) {
        const previousDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.previous.duration],
        );
        const previousDuration = `${metadata.previous.value} ${previousDurationPeriod}`;
        return (
          nameAndDateString +
          '\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: previousDuration,
            toValue: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_LENGTH'),
          })
        );
      }
      return (
        nameAndDateString +
        '\n' +
        this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
          value: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_LENGTH'),
        })
      );
    }
    if (metadata instanceof SubmittedForCustomerApprovalSendingMetadata) {
      let resultString = nameAndDateString;
      if (order?.customerRecipient?.firstName || order?.customerRecipient?.lastName) {
        resultString =
          resultString +
          '\n' +
          'Sent to ' +
          this.formatDisplayName(order.customerRecipient.firstName ?? '', order.customerRecipient.lastName ?? '');
      }
      if (metadata?.offerExpiry) {
        return resultString + '\n' + 'Expires ' + this.formatDateWithTime(metadata.offerExpiry);
      }
      return resultString;
    }
    return nameAndDateString;
  }

  private formatDate(date: Date): string {
    return new DatePipe('en-US').transform(date, 'MMMM d, y');
  }
}
